"use client";

import {
  browserTimer,
  getEhrExtensionBridge,
} from "@/utils/ehr-extension/bridge";
import {
  type RecordingLeaseHandle,
  acquireRecordingLease,
  hasActiveRecordingLease,
} from "@/utils/ehr-extension/recording-lease";
import {
  useGlobalTranscriptionStatus,
  usePipState,
} from "@medimo/global-state";
import * as logger from "@medimo/monitor/logger";
import { ToastAction, toast } from "@medimo/ui";
import { useEffect } from "react";

export const RECORDING_LEASE_REFUSED_TEXT =
  "拡張機能で録音中のため、録音を開始しませんでした。";
export const DOUBLE_RECORDING_TEXT = "拡張機能でも録音しています。";

/**
 * 拒否のトーストを出す時間。ほかのトーストの 5 秒では、文を読んで「このまま録音する」
 * を押す前に消える
 */
export const RECORDING_LEASE_REFUSED_DURATION_MS = 15_000;

const isRecording = (status: string) =>
  status === "transcribing" || status === "pause";

export type StartRecordingOptions = {
  /** 医師が拡張機能の録音を確かめたうえで始める。拡張機能に問わない */
  skipExtension?: boolean;
};

/** どちらの録音も止めないため、医師がどちらを止めるか決めるまで残す */
const showDoubleRecordingWarning = () => {
  toast({
    title: DOUBLE_RECORDING_TEXT,
    variant: "destructive",
    duration: Number.POSITIVE_INFINITY,
  });
};

/** 開始の対象を決める画面の状態。画面のパスと、ミニ画面で選んでいる診察 */
const startTarget = () =>
  `${typeof window === "undefined" ? "" : window.location.pathname}|${usePipState.getState().selectedConsultId}`;

/**
 * 拒否を知らせ、医師が選べば拡張機能に問わずに始める。不具合で誤って拒否しても、
 * 医師がその場で録音を始められるようにするためである。`target` は開始の操作を
 * した時点の画面の状態で、押したときに別の画面へ移るか、ミニ画面で別の診察を
 * 選んでいたら始めない。画面に出ていない診察で録音が始まるためである
 */
export const showRecordingLeaseRefusal = (
  start: (options: StartRecordingOptions) => Promise<void>,
  target: string,
) => {
  const startAnyway = async () => {
    const status = useGlobalTranscriptionStatus.getState();
    if (
      !status.canStart ||
      status.status !== "inactive" ||
      startTarget() !== target
    ) {
      return;
    }
    status.setCanStart(false);
    try {
      await start({ skipExtension: true });
    } catch (error) {
      logger.error("startRecordingWithoutExtension:failed", {
        error: String(error),
      });
      toast({
        title: "録音を開始できませんでした。もう一度お試しください。",
        variant: "destructive",
      });
    } finally {
      useGlobalTranscriptionStatus.getState().setCanStart(true);
    }
  };
  toast({
    title: RECORDING_LEASE_REFUSED_TEXT,
    variant: "destructive",
    duration: RECORDING_LEASE_REFUSED_DURATION_MS,
    action: (
      <ToastAction
        altText="拡張機能に問わずに録音を開始する"
        onClick={() => {
          startAnyway().catch(() => undefined);
        }}
      >
        このまま録音する
      </ToastAction>
    ),
  });
};

/**
 * 開始の手順の先頭で録音リースを取得する。拒否されたら null を返す。拒否のほかは、
 * 例外が起きても録音を始められるようにリースを返すか undefined を返す
 */
export const acquireLeaseForStart = async (
  start: (options: StartRecordingOptions) => Promise<void>,
  options: StartRecordingOptions | undefined,
): Promise<RecordingLeaseHandle | null | undefined> => {
  // 拡張機能の返事を待つ間に画面や選択が変わることがあるため、操作した時点で読む
  const target = startTarget();
  try {
    const bridge = getEhrExtensionBridge();
    bridge?.start();
    const result = await acquireRecordingLease({
      bridge,
      timer: browserTimer,
      onConflict: showDoubleRecordingWarning,
      isRecording: () =>
        isRecording(useGlobalTranscriptionStatus.getState().status),
      skipExtension: options?.skipExtension,
    });
    if (result.refused) {
      showRecordingLeaseRefusal(start, target);
      return null;
    }
    return result.lease;
  } catch {
    return undefined;
  }
};

/**
 * 拡張機能の content script とのメッセージの受け渡しを始め、拡張機能に問われたら録音中か開始の準備中かを答える。
 * 認証の画面にいるタブも答えられるよう、認証の画面かどうかに関わらず root で動かす
 */
export const useEhrExtensionBridge = () => {
  useEffect(() => {
    try {
      const bridge = getEhrExtensionBridge();
      if (bridge === null) return;
      bridge.setBusy(
        () =>
          hasActiveRecordingLease() ||
          isRecording(useGlobalTranscriptionStatus.getState().status),
      );
      bridge.start();
    } catch {
      // 受け渡しを始められなくても、medimo Web は拡張機能が無いものとして録音できる
    }
  }, []);
};

/**
 * 開始の手順を実行する前に録音リースを取得し、拒否されたら手順を実行しない。
 * 手順が途中で return しても例外を投げても、録音処理が引き継いでいないリースを解放する
 */
export const withRecordingLease = async (
  options: StartRecordingOptions | undefined,
  restart: (options: StartRecordingOptions) => Promise<void>,
  body: (lease: RecordingLeaseHandle | undefined) => Promise<void>,
): Promise<void> => {
  const lease = await acquireLeaseForStart(restart, options);
  if (lease === null) return;
  try {
    await body(lease);
  } finally {
    try {
      lease?.releaseIfUnclaimed();
    } catch {
      // 解放を送れなくても、拡張機能は heartbeat の途絶で失効させる
    }
  }
};
